import {
  Card,
  CardSection,
  Group,
  Stack,
  Text,
  Button,
  Skeleton,
  Chip,
  CloseButton,
  ActionIcon,
  Grid,
} from "@mantine/core";
import {
  IconBan,
  IconBuilding,
  IconCategory,
  IconChevronLeft,
  IconX,
  IconBed,
  IconLocationSearch,
  IconMapPin,
} from "@tabler/icons-react";
import { useEffect, useState } from "react";
// import { PlaceInfo, PlacesService, sortPlacesByDistance } from '../../services/places.service';
// import { LatLng, Stop, VehicleStopPurpose, VehicleStopType } from '../../store/config/types';
import { getCustomerDisplayName } from "../../utils/string.helpers";
import {
  PlaceInfo,
  PlacesService,
  sortPlacesByDistance,
} from "services/places.service";
import {
  HasLatLong,
  TripStopResponse,
  VehicleStopPurpose,
  VehicleStopResponse,
  VehicleStopType,
} from "types/trip.types";
import TripVehicleStopCard from "components/trips/TripVehicleStopCard";

interface IdentifyStopPanelProps {
  stop: VehicleStopResponse;
  location: { latitude: number; longitude: number };
  titleBar?: boolean;
  options?: {
    displayImage?: boolean;
  };
  onClose?: () => void;
  // eslint-disable-next-line
  onAction?: (action: string, params?: any) => any;
}

export default function IdentifyStopPanel({
  stop,
  location,
  titleBar,
  options,
  onClose,
  onAction,
}: IdentifyStopPanelProps) {
  const [loading, setLoading] = useState(false);
  const [places, setPlaces] = useState<PlaceInfo[]>([]);
  const [selectedPlace, setSelectedPlace] = useState<number>();

  useEffect(() => {
    setSelectedPlace(undefined);
  }, [places]);

  const handleNearbySearchClick = () => {
    if (location) {
      setLoading(true);
      PlacesService.placeNearbySearch(location, "GOOGLE_PLACES")
        .then((data) => {
          if (data) {
            setPlaces(data.places.map((p, idx) => ({ ...p, placeId: idx })));
            if (onAction) {
              if (data?.vicinity) {
                onAction(
                  "title-change",
                  `${location.latitude}, ${location.longitude} (${data.vicinity})`
                );
              }
              if (data.places.length) {
                const boundPoints: HasLatLong[] = [];
                data.places?.forEach((p, pIndex) => {
                  boundPoints.push({
                    latitude: p.latitude,
                    longitude: p.longitude,
                  });
                  p.label = pIndex + 1;
                });
                onAction("map-bound-change", boundPoints);
              }
            }
          }
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const handleCustomerSearchClick = () => {
    if (onAction) {
      try {
        const tripStops: TripStopResponse[] = onAction("get-trip-stops");
        const customerPlaces: PlaceInfo[] = tripStops
          .filter((ts) => ts.orders?.length)
          .map((ts, tsIndex) => ({
            latitude: ts.place?.latitude ?? 0,
            longitude: ts.place?.longitude ?? 0,
            name: getCustomerDisplayName(ts.customer as any),
            address: ts.place?.address,
            type: "CUSTOMER",
            label: tsIndex + 1,
            additionalData: {
              tripStopId: ts.tripStopId,
              customerId: ts.customer?.customerId,
            },
            placeId: tsIndex,
          }));
        customerPlaces.sort((p1, p2) =>
          sortPlacesByDistance(
            { latitude: location.latitude, longitude: location.longitude },
            p1,
            p2
          )
        );
        if (customerPlaces.length) {
          onAction("map-bound-change", [
            {
              lat: customerPlaces[0].latitude,
              lng: customerPlaces[0].longitude,
            },
            {
              lat: location.latitude,
              lng: location.longitude,
            },
          ]);
        }
        console.log({ customerPlaces });

        setPlaces(customerPlaces);
      } catch (err) {
        console.error(err);
      }
    }
  };

  const handleMenuActions = (menu: string) => {
    let identifiedType: VehicleStopType | undefined = undefined;
    let identifiedPurpose: VehicleStopPurpose | undefined = undefined;
    switch (menu) {
      case "goto-main":
        setPlaces([]);
        setLoading(false);
        if (onAction) onAction("map-clear-markers");
        break;
      case "Customer-Specific":
        handleCustomerSearchClick();
        break;
      case "Search Nearby":
        handleNearbySearchClick();
        break;
      case "General":
        identifiedType = VehicleStopType.UNRELATED;
        identifiedPurpose = VehicleStopPurpose.GENERAL;
        break;
      case "Overnight Stay":
        identifiedType = VehicleStopType.UNRELATED;
        identifiedPurpose = VehicleStopPurpose.REST;
        break;
      case "Pickup point":
        identifiedType = VehicleStopType.PICKUP;
        identifiedPurpose = VehicleStopPurpose.PICKUP;
        break;
      case "Unrelated Stop":
        identifiedType = VehicleStopType.UNRELATED;
        identifiedPurpose = VehicleStopPurpose.UNRELATED;
        break;
      default:
        alert(menu);
    }
    if (identifiedType || identifiedPurpose) {
      if (onAction)
        onAction("identify-stop-type", {
          stop,
          type: identifiedType,
          purpose: identifiedPurpose,
        });
    }
  };

  const handlePlaceAction = (place: PlaceInfo, action: string) => {
    switch (action) {
      case "customer":
        handleMenuActions("Customer-Specific");
        break;
      case "unrelated":
        handleMenuActions("Unrelated Stop");
        break;
      case "general":
        handleMenuActions("General");
        break;
      case "select":
        if (onAction)
          onAction("identify-stop-type", {
            stop,
            type: "CUSTOMER",
            purpose: "DROP_OFF",
            additionalData: place.additionalData,
          });
        break;
    }
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };
  const handlePlaceHover = (place: PlaceInfo) => {
    if (onAction) {
      onAction("map-display-markers", [
        {
          position: { lat: place.latitude, lng: place.longitude },
          label: place.label,
        },
      ]);
    }
  };
  const handlePlaceClick = (place: PlaceInfo) => {
    setSelectedPlace(place.placeId);
    if (onAction) {
      onAction("map-bound-change", [
        {
          lat: place.latitude,
          lng: place.longitude,
        },
        {
          lat: location.latitude,
          lng: location.longitude,
        },
      ]);
    }
  };

  return (
    <Stack p="md" gap="md">
      <Group mb="sm">
        <ActionIcon onClick={handleClose} variant="subtle">
          <IconChevronLeft />
        </ActionIcon>
      </Group>
      <TripVehicleStopCard stop={stop} onAction={onAction} />
      {/* Title bar */}
      {titleBar && (
        <Group justify="space-between">
          <Text>
            {location.latitude},{location.longitude}
          </Text>
          <ActionIcon onClick={handleClose} size="sm" variant="subtle">
            <IconX size={18} />
          </ActionIcon>
        </Group>
      )}
      <div>
        {places.length == 0 && !loading && (
          <PlacesTypeMenu handleAction={handleMenuActions} />
        )}
        {loading ? (
          <>
            <PlaceSkeleton />
            <PlaceSkeleton opacity={0.8} />
            <PlaceSkeleton opacity={0.6} />
            <PlaceSkeleton opacity={0.4} />
            <PlaceSkeleton opacity={0.2} />
          </>
        ) : (
          places.length > 0 && (
            <>
              <Group mb="sm">
                <ActionIcon
                  onClick={() => {
                    handleMenuActions("goto-main");
                  }}
                  variant="subtle"
                >
                  <IconChevronLeft />
                </ActionIcon>
              </Group>
              {places.map((place, pIndex) => (
                <PlaceCard
                  key={pIndex}
                  place={place}
                  index={place.label ?? pIndex + 1}
                  options={options}
                  onHover={handlePlaceHover}
                  onClick={handlePlaceClick}
                  selected={
                    place.placeId != undefined && place.placeId == selectedPlace
                  }
                  onAction={handlePlaceAction}
                />
              ))}
            </>
          )
        )}
      </div>
    </Stack>
  );
}

const PlaceSkeleton = ({ opacity }: { opacity?: number }) => (
  <Group mb="sm" gap="xs" style={{ opacity }}>
    <Skeleton height={24} width={24} radius="sm" />
    <Stack gap={4} style={{ flex: 1 }}>
      <Group gap={4}>
        <Skeleton height={24} width={24} circle />
        <Skeleton height={24} width={150} />
      </Group>
      <Skeleton height={12} width="100%" />
      <Skeleton height={12} width="70%" />
    </Stack>
  </Group>
);

const PlaceCard = ({
  place,
  index,
  selected,
  onClick,
  onHover,
  onAction,
}: {
  place: PlaceInfo;
  index: number;
  selected?: boolean;
  onClick: any;
  onHover: any;
  onAction: any;
  options?: any;
}) => {
  return (
    <Card mb="sm" shadow={selected ? "md" : "xs"} withBorder>
      <CardSection
        onClick={() => onClick(place)}
        onMouseEnter={() => onHover(place)}
        style={{ cursor: "pointer" }}
      >
        <Group align="flex-start" gap="xs" wrap="nowrap">
          <div
            style={{
              width: 24,
              height: 24,
              backgroundColor: place.iconBackgroundColor ?? "#ccc",
              maskImage: `url(${place.icon})`,
              maskSize: "24px 24px",
              maskRepeat: "no-repeat",
              borderRadius: 5,
            }}
          />
          <Stack gap={0} style={{ flex: 1 }}>
            <Group gap={4} wrap="nowrap">
              <Chip size="xs" variant="filled">
                {index}
              </Chip>
              <Text size="sm" w={500}>
                {place.name}
              </Text>
            </Group>
            <Text size="xs" color="dimmed">
              {place.address}
            </Text>
          </Stack>
        </Group>
      </CardSection>
      {selected && (
        <CardSection>
          <Group justify="flex-end" gap="xs">
            {place.type == "CUSTOMER" ? (
              <Button
                size="xs"
                onClick={() => {
                  onAction(place, "select");
                }}
              >
                Select
              </Button>
            ) : (
              <>
                <Button
                  size="xs"
                  onClick={() => {
                    onAction(place, "general");
                  }}
                >
                  General
                </Button>
                <Button
                  size="xs"
                  onClick={() => {
                    onAction(place, "customer");
                  }}
                >
                  Customer
                </Button>
                <Button
                  size="xs"
                  onClick={() => {
                    onAction(place, "unrelated");
                  }}
                >
                  Unrelated
                </Button>
              </>
            )}
          </Group>
        </CardSection>
      )}
    </Card>
  );
};

const PlacesTypeMenu = ({ handleAction }: { handleAction: any }) => {
  return (
    <>
      <PlacesTypeMenuItem
        title="General"
        description="Applicable to all customers on this trip."
        onAction={handleAction}
      />
      <PlacesTypeMenuItem
        title="Customer-Specific"
        description="Relevant to certain customer."
        icon={<IconBuilding />}
        onAction={handleAction}
      />
      <PlacesTypeMenuItem
        title="Overnight Stay"
        description="Applicable to all customers on this trip."
        icon={<IconBed />}
        onAction={handleAction}
      />
      <PlacesTypeMenuItem
        title="Pickup point"
        description="Designate locations as pickup points."
        icon={<IconMapPin />}
        onAction={handleAction}
      />
      <PlacesTypeMenuItem
        title="Unrelated Stop"
        description="Not associated with the current trip."
        icon={<IconBan />}
        onAction={handleAction}
      />
      <PlacesTypeMenuItem
        title="Search Nearby"
        description="Find locations or services near this area."
        icon={<IconLocationSearch />}
        onAction={handleAction}
      />
    </>
  );
};

const PlacesTypeMenuItem = ({
  title,
  description,
  icon,
  onAction,
}: {
  title: string;
  description?: string;
  icon?: any;
  onAction?: any;
}) => {
  return (
    <Card mb="sm" withBorder>
      <CardSection
        onClick={() => {
          if (onAction) onAction(title);
        }}
        style={{ cursor: "pointer" }}
      >
        <Group align="flex-start" gap="xs" wrap="nowrap">
          {icon ?? <IconCategory />}
          <Stack gap={0} style={{ flex: 1 }}>
            <Text size="sm" w={500}>
              {title}
            </Text>
            {Boolean(description) && (
              <Text size="xs" c="dimmed">
                {description}
              </Text>
            )}
          </Stack>
        </Group>
      </CardSection>
    </Card>
  );
};
